import {
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { MdInfoOutline } from 'react-icons/md';
import * as nfseService from 'services/nfse';

interface IssueManyModalProps {
  isOpen: boolean;
  onClose(): void;
}

const getFirstDayOfMonth = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01 00:00:00`;
};

const getLastDayOfMonth = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const lastDay = new Date(year, month, 0).getDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')} 23:59:59`;
};

const IssueManyModal: React.FC<IssueManyModalProps> = ({ isOpen, onClose }) => {
  const toast = useToast();

  const [startDate, setStartDate] = useState(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState(getLastDayOfMonth());
  const [maxOrders, setMaxOrders] = useState(500);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setStartDate(getFirstDayOfMonth());
      setEndDate(getLastDayOfMonth());
      setMaxOrders(500);
      setSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!startDate || !endDate) {
      toast({
        title: 'Atenção',
        description: 'Preencha as datas de início e fim',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (maxOrders <= 0) {
      toast({
        title: 'Atenção',
        description: 'O limite de pedidos deve ser maior que zero',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSubmitting(true);

    try {
      const response = await nfseService.issueManyNfse(
        startDate,
        endDate,
        maxOrders,
      );

      const { totalEnqueued } = response.data;

      toast({
        title: 'Sucesso',
        description: `${totalEnqueued} DPS(s) adicionadas a fila para emissão.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onClose();
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Erro ao emitir DPS em lote';
      toast({
        title: 'Erro',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      closeOnOverlayClick={!submitting}
      closeOnEsc={!submitting}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
    >
      <ModalOverlay />

      <ModalContent>
        <ModalCloseButton isDisabled={submitting} />
        <ModalHeader>Emissão Mensal</ModalHeader>

        <ModalBody>
          <Stack spacing={4}>
            <Card bg="blue.100" marginBottom="18px">
              <CardBody>
                <HStack gap="1rem">
                  <Icon as={MdInfoOutline} color="blue.500" />
                  <Text fontSize="0.9rem" fontWeight="400" color="gray.600">
                    Esse fluxo realiza a emissão de DPS para as{' '}
                    <strong>cobranças pagas</strong>, com o código NBS{' '}
                    <strong>010501</strong>.
                  </Text>
                </HStack>
              </CardBody>
            </Card>
            <HStack spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">Data Início</FormLabel>
                <Input
                  size="sm"
                  type="date"
                  value={startDate.split(' ')[0]}
                  onChange={(e) => setStartDate(`${e.target.value} 00:00:00`)}
                  isDisabled={submitting}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Data Fim</FormLabel>
                <Input
                  size="sm"
                  type="date"
                  value={endDate.split(' ')[0]}
                  onChange={(e) => setEndDate(`${e.target.value} 23:59:59`)}
                  isDisabled={submitting}
                />
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel fontSize="sm">Limite de Cobranças</FormLabel>
              <Input
                size="sm"
                type="number"
                min={1}
                value={maxOrders}
                onChange={(e) => setMaxOrders(Number(e.target.value))}
                isDisabled={submitting}
              />
            </FormControl>
          </Stack>
        </ModalBody>

        <ModalFooter>
          <HStack>
            <Button variant="ghost" onClick={onClose} isDisabled={submitting}>
              Cancelar
            </Button>
            <Button
              colorScheme="yooga"
              onClick={handleSubmit}
              isLoading={submitting}
              loadingText="Emitindo"
            >
              Emitir
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default IssueManyModal;
